/**
 * Aircraft Types - Global reference data for aircraft models
 */

/** Aircraft category classification */
export type AircraftCategory =
  | 'single_engine_piston'
  | 'multi_engine_piston'
  | 'turboprop'
  | 'very_light_jet'
  | 'light_jet'
  | 'midsize_jet'
  | 'super_midsize_jet'
  | 'large_jet'
  | 'ultra_long_range'
  | 'helicopter'

/** Aircraft category display labels */
export const AIRCRAFT_CATEGORY_LABELS: Record<AircraftCategory, string> = {
  single_engine_piston: 'Single Engine Piston',
  multi_engine_piston: 'Multi Engine Piston',
  turboprop: 'Turboprop',
  very_light_jet: 'Very Light Jet (VLJ)',
  light_jet: 'Light Jet',
  midsize_jet: 'Midsize Jet',
  super_midsize_jet: 'Super Midsize Jet',
  large_jet: 'Large Jet',
  ultra_long_range: 'Ultra Long Range',
  helicopter: 'Helicopter',
}

/** All aircraft categories for select dropdowns */
export const AIRCRAFT_CATEGORIES: AircraftCategory[] = [
  'single_engine_piston',
  'multi_engine_piston',
  'turboprop',
  'very_light_jet',
  'light_jet',
  'midsize_jet',
  'super_midsize_jet',
  'large_jet',
  'ultra_long_range',
  'helicopter',
]

/** Gear type for landing gear configuration */
export type GearType = 'tricycle' | 'conventional' | 'tandem'

/** Gear type display labels */
export const GEAR_TYPE_LABELS: Record<GearType, string> = {
  tricycle: 'Tricycle (Nose Gear)',
  conventional: 'Conventional (Taildragger)',
  tandem: 'Tandem',
}

/** Database row type for aircraft_types table */
export interface AircraftTypeRow {
  id: string
  type_code: string
  manufacturer: string
  model: string
  variant: string | null
  category: AircraftCategory | null
  default_fuel_type: 'jet_a' | 'avgas' | null

  // Dimensions (feet)
  length: number
  wingspan: number
  height: number
  tail_height: number | null

  // Dimensions (meters)
  wingspan_m: number | null
  length_m: number | null
  height_m: number | null

  // Footprint
  footprint_sqft: number | null
  footprint_sqm: number | null
  footprint_polygon: [number, number][] | null

  // Weight (pounds)
  empty_weight: number | null
  max_takeoff_weight: number | null
  max_ramp_weight: number | null

  // 3D Model
  /** Public URL of the GLTF model (DB column; returned by `select('*')`) */
  model_url: string | null
  model_file_path: string | null
  model_scale: number | null
  heading_axis: string | null
  vertical_axis: string | null

  // Pricing
  typical_value_new: number | null
  typical_value_used: number | null
  hangar_rate_typical: number | null

  // Metadata
  notes: string | null
  is_active: boolean | null

  // Landing Gear / Towing fields
  gear_type: GearType | null
  main_gear_wheels: number | null
  nose_gear_steering_angle: number | null
  nose_gear_from_nose: number | null
  main_gear_from_nose: number | null
  wheel_track: number | null
  wheelbase: number | null
  turning_radius: number | null

  // External API reference
  icao_designator: string | null
  external_api_id: number | null
  last_api_sync: string | null

  // Timestamps
  created_at: string
  updated_at: string | null
}

/** Insert type for creating new aircraft types */
export interface AircraftTypeInsert {
  type_code: string
  manufacturer: string
  model: string
  variant?: string | null
  category?: AircraftCategory | null
  length: number
  wingspan: number
  height: number
  tail_height?: number | null
  wingspan_m?: number | null
  length_m?: number | null
  height_m?: number | null
  footprint_sqft?: number | null
  footprint_sqm?: number | null
  footprint_polygon?: [number, number][] | null
  empty_weight?: number | null
  max_takeoff_weight?: number | null
  max_ramp_weight?: number | null
  model_file_path?: string | null
  model_scale?: number | null
  heading_axis?: string | null
  vertical_axis?: string | null
  typical_value_new?: number | null
  typical_value_used?: number | null
  hangar_rate_typical?: number | null
  notes?: string | null
  is_active?: boolean | null

  // Landing Gear / Towing fields
  gear_type?: GearType | null
  main_gear_wheels?: number | null
  nose_gear_steering_angle?: number | null
  nose_gear_from_nose?: number | null
  main_gear_from_nose?: number | null
  wheel_track?: number | null
  wheelbase?: number | null
  turning_radius?: number | null

  // External API reference
  icao_designator?: string | null
  external_api_id?: number | null
  last_api_sync?: string | null
}

/** Update type for modifying aircraft types */
export interface AircraftTypeUpdate {
  type_code?: string
  manufacturer?: string
  model?: string
  variant?: string | null
  category?: AircraftCategory | null
  length?: number
  wingspan?: number
  height?: number
  tail_height?: number | null
  wingspan_m?: number | null
  length_m?: number | null
  height_m?: number | null
  footprint_sqft?: number | null
  footprint_sqm?: number | null
  footprint_polygon?: [number, number][] | null
  empty_weight?: number | null
  max_takeoff_weight?: number | null
  max_ramp_weight?: number | null
  model_file_path?: string | null
  model_scale?: number | null
  heading_axis?: string | null
  vertical_axis?: string | null
  typical_value_new?: number | null
  typical_value_used?: number | null
  hangar_rate_typical?: number | null
  notes?: string | null
  is_active?: boolean | null

  // Landing Gear / Towing fields
  gear_type?: GearType | null
  main_gear_wheels?: number | null
  nose_gear_steering_angle?: number | null
  nose_gear_from_nose?: number | null
  main_gear_from_nose?: number | null
  wheel_track?: number | null
  wheelbase?: number | null
  turning_radius?: number | null

  // External API reference
  icao_designator?: string | null
  external_api_id?: number | null
  last_api_sync?: string | null
}

/** Filter options for aircraft types list */
export interface AircraftTypeFilters {
  search?: string
  category?: AircraftCategory | null
  isActive?: boolean
}

/** Infer fuel type from aircraft category (pistons → avgas, everything else → jet_a) */
export function inferFuelTypeFromCategory(category: AircraftCategory | string | null): 'jet_a' | 'avgas' {
  return category === 'single_engine_piston' || category === 'multi_engine_piston'
    ? 'avgas' : 'jet_a'
}
